/*
 * Bundle & Save selection state.
 *
 * This element does NOT calculate or format prices. Every tier x interval total is
 * rendered server-side by `snippets/bundle-save.liquid` through the `money` filter;
 * all this does is decide which of those pre-rendered spans is visible, keep the two
 * hidden form inputs in sync, and mirror the running total into Dawn's add-to-cart
 * label. The block is already correct at first paint and with JS disabled.
 *
 * Visibility rule (shared with the Liquid): a `[data-price-key]` span is visible when
 * its key matches the current selection for its *enclosing* `[data-tier]` element —
 * the tier button for a tier card, the `<bundle-save>` root for the total row.
 */
if (!customElements.get('bundle-save')) {
  /*
   * How long a variant swap will wait for the incoming tier images before going ahead
   * without them. Long enough to cover a normal CDN round trip, short enough that the
   * prices never visibly lag the picker on a bad connection. See warmImages.
   *
   * Declared inside the guard, not at the top of the file: this script can be evaluated
   * twice (that is what the guard is for), and a re-declared top-level `const` throws.
   */
  const IMAGE_WARM_DEADLINE = 400;

  /*
   * How much taller than its shorter state the purchase block is held, so switching moves
   * the page less without parking the shorter state over a void. See lockOptionsHeight.
   */
  const OPTIONS_SLACK = 50;

  customElements.define(
    'bundle-save',
    class BundleSave extends HTMLElement {
      /*
       * Bind once here, not in connectedCallback — the theme editor moves blocks around,
       * and re-binding on each connect would hand removeEventListener a different
       * function object than the one that was added, leaking a duplicate click listener.
       */
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onPointerModality = () => this.setModality('pointer');
        this.onKeyModality = (event) => {
          if (event.key === 'Tab' || event.key.startsWith('Arrow')) this.setModality('keyboard');
        };
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.cacheRefs();
        this.claimQuantity(Boolean(this.qtyInput));
        this.warnOnStayCollision();

        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);

        /*
         * Which input device last drove focus, so the CSS can drop the
         * cadence select's focus ring for a mouse click and keep it for Tab. Chrome
         * matches `:focus-visible` on a <select> however it was focused, so CSS alone
         * cannot tell the two apart. On `document`, not on this element — a Tab that
         * arrives from outside the block is a keydown that never reaches it. Capture
         * phase so it is recorded before focus moves.
         */
        document.addEventListener('pointerdown', this.onPointerModality, true);
        document.addEventListener('keydown', this.onKeyModality, true);

        // Degrade cleanly when Dawn's pubsub is absent: the block keeps working,
        // just without variant reactivity.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.render();
        this.lockOptionsHeight();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        document.removeEventListener('pointerdown', this.onPointerModality, true);
        document.removeEventListener('keydown', this.onKeyModality, true);
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
        // Removed in the editor → give the quantity back to Dawn's selector.
        this.claimQuantity(false);
      }

      cacheRefs() {
        this.planInput = this.querySelector('[data-bundle-plan]');
        this.qtyInput = this.querySelector('[data-bundle-qty]');
        this.totalEl = this.querySelector('[data-bundle-total]');
        this.intervalsEl = this.querySelector('[data-bundle-intervals]');
      }

      /*
       * When the block renders pack tiers, the tier IS the quantity, so this block owns
       * the form's `quantity` and Dawn's `quantity_selector` must not compete: its input
       * is disabled (disabled inputs don't submit) and its wrapper hidden. When the block
       * has no tiers — intervals-only mode, or gated off entirely after a variant change —
       * Dawn's selector is restored. With JS disabled neither happens; that case is
       * covered by block order in the template: this block sits after quantity_selector,
       * so its own hidden quantity input serializes later in the form and wins.
       */
      claimQuantity(own) {
        const formId = this.dataset.formId;
        if (!formId) return;

        const escaped = CSS.escape(formId);
        document
          .querySelectorAll(
            `input[form="${escaped}"][name="quantity"]:not([data-bundle-qty]), ` +
              `#${escaped} input[name="quantity"]:not([data-bundle-qty])`,
          )
          .forEach((input) => {
            input.disabled = own;
            const wrapper = input.closest('.product-form__quantity') ?? input.closest('quantity-input');
            if (wrapper) wrapper.hidden = own;
          });
      }

      /*
       * Both this block and Stay.ai's app block selling the same product means two
       * conflicting subscription pickers. The fix is to keep Stay.ai's app block off any
       * product template that bulk products use — so this only reports the collision in
       * the editor. It deliberately does not hide the other app's block.
       */
      warnOnStayCollision() {
        if (!window.Shopify?.designMode) return;

        const scope = this.closest('.shopify-section') ?? document;
        const stayWidget = scope.querySelector('[id^="rtx-subscription-widget"], [data-rtx-widget], rtx-pdp');
        if (!stayWidget) return;

        const notice = this.querySelector('[data-bundle-collision]');
        if (notice) notice.hidden = false;
        console.warn('[bundle-save] Stay.ai subscription widget detected in the same section — remove one of them.');
      }

      priceKeyFor(tier) {
        return this.dataset.purchase === 'onetime' ? `onetime-${tier}` : `sub-${tier}-${this.dataset.interval}`;
      }

      render() {
        this.querySelectorAll('[data-price-key]').forEach((el) => {
          const scope = el.closest('[data-tier]');
          el.hidden = !scope || el.dataset.priceKey !== this.priceKeyFor(scope.dataset.tier);
        });

        // Purchase-type-independent spans: subscription card prices (tier + interval)
        // and per-tier content (pack-size name, one-time price, the tier's cadence select).
        const subKey = `${this.dataset.tier}-${this.dataset.interval}`;
        this.querySelectorAll('[data-sub-key]').forEach((el) => {
          el.hidden = el.dataset.subKey !== subKey;
        });
        this.querySelectorAll('[data-tier-key]').forEach((el) => {
          el.hidden = el.dataset.tierKey !== this.dataset.tier;
        });

        this.setPressed('data-purchase-option', this.dataset.purchase);
        this.setPressed('data-tier-option', this.dataset.tier);
        this.setPressed('data-interval-option', this.dataset.interval);

        if (this.intervalsEl) {
          /*
           * The body's collapse is a CSS grid-row animation keyed on the card's
           * `data-selected`, and `hidden` would short-circuit it with `display: none`.
           * So the attribute is cleared here and `inert` carries what it was actually
           * for — keeping a closed body out of the tab order. The Liquid still renders
           * `hidden`, which is what makes the no-JS fallback correct: with no JS to
           * clear it, the body stays collapsed.
           */
          const closed = this.dataset.purchase === 'onetime';
          this.intervalsEl.hidden = false;
          this.intervalsEl.inert = closed;
        }

        const selected = this.totalEl?.querySelector(
          `[data-price-key="${CSS.escape(this.priceKeyFor(this.dataset.tier))}"]`,
        );

        // Empty string means "no selling plan" — a plain one-time purchase.
        if (this.planInput) this.planInput.value = selected?.dataset.planId ?? '';
        if (this.qtyInput) this.qtyInput.value = this.dataset.tier;

        this.placeBuyButtons();
      }

      /* Absent until something has been observed, so the ring — the accessible state —
         is what a fresh page gets. */
      setModality(mode) {
        this.dataset.modality = mode;
      }

      /*
       * Reserve the taller of the two states on the wrapper, so switching
       * purchase type does not move everything below the block. Measured: 371px with
       * subscription selected against 258px with one-time, so the page under it jumped
       * 113px on every switch.
       *
       * It has to be measured rather than declared. The taller state depends on the
       * product's benefit lines, how the labels wrap, and the viewport, so there is no
       * honest number to put in the stylesheet — and which state is taller is not fixed
       * either, since a product can default to one-time.
       *
       * Both states are rendered and measured in one task with the flip forced to 0s, so
       * nothing paints in between and the shopper never sees the intermediate state.
       * `min-height` rather than `height`: if a state does turn out taller than what was
       * measured — a longer money string after a variant change, say — it grows instead of
       * clipping the add-to-cart.
       */
      lockOptionsHeight() {
        const options = this.querySelector('.bundle-save__options');
        if (!options) return;

        const previous = this.dataset.purchase;
        options.style.setProperty('--bs-once-flip', '0s');
        options.style.minHeight = '';

        const heights = [];
        for (const purchase of ['subscription', 'onetime']) {
          this.dataset.purchase = purchase;
          this.render();
          heights.push(options.getBoundingClientRect().height);
        }

        /*
         * Not the taller state — the shorter one plus a bit of slack, capped so the
         * reservation can never exceed what the block actually needs.
         *
         * Reserving the full difference removes the movement but leaves the shorter state
         * sitting over a large void (113px on this product). Reserving nothing puts the
         * whole 113px back into the page as a jump. Splitting it takes most of the travel
         * out while keeping the empty space to something that reads as breathing room.
         */
        const shortest = Math.min(...heights);
        const tallest = Math.max(...heights);
        const reserved = Math.min(tallest, shortest + OPTIONS_SLACK);

        this.dataset.purchase = previous;
        this.render();

        if (reserved > 0) options.style.minHeight = `${Math.ceil(reserved)}px`;

        /*
         * Transitions come back a frame later, not in this task.
         *
         * Restoring the state and re-enabling them together lets the browser treat the
         * restore as something to animate: it starts transitioning out of whichever state
         * was measured last, and the card is left sitting collapsed with `data-selected`
         * saying otherwise. Reading offsetHeight commits the restored layout at 0s first,
         * so there is nothing left to animate when the duration comes back.
         */
        void options.offsetHeight;
        requestAnimationFrame(() => options.style.removeProperty('--bs-once-flip'));
      }

      /*
       * The add-to-cart button sits inside whichever card is selected.
       *
       * Dawn's own node is moved rather than a second button rendered beside it, so the
       * loading state, sold-out disabling and error handling keep working — product-form.js
       * caches `submitButton` in its constructor, and product-info's `toggleSubmitButton`
       * writes through that same cached reference, so neither notices the node has moved.
       *
       * The WHOLE <product-form> travels, not just the button. Moving the button alone
       * looks like it works and is not: `onSubmitHandler` reaches for its spinner with
       * `this.querySelector('.loading__spinner')`, scoped to <product-form>, and the
       * spinner lives inside the button. Lift the button out and that returns null, the
       * handler throws on line 28, and the button sticks in its loading state having added
       * nothing to the cart. Taking the element whole keeps every such lookup intact and
       * needs no `form` attribute, because the button is still inside its own form.
       *
       * Only ever moved into the SELECTED card, so it never lands in the subscription
       * body while that body is collapsed and inert.
       *
       * Called from render(), so it re-asserts itself after a variant swap without needing
       * its own hook.
       */
      placeBuyButtons() {
        const button = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        const productForm = button?.closest('product-form');
        const slot = this.querySelector('[data-selected="true"] [data-bundle-cta]');
        if (!productForm || !slot || productForm.parentElement === slot) return;

        /*
         * ⚠️ This move is instantaneous and there is a visible ~57px single-frame step
         * because of it — the card losing the button loses that height at once while both
         * cards are mid-animation. It is the one part of the flip that is not smooth.
         *
         * Three fixes were tried and measured, none of which worked, so do not reach for
         * them again:
         *   - pinning an explicit height on the wrapper: fights the grid track it sits in,
         *     the row closes underneath it and the card barely collapses at all
         *     (177.6 -> 172.6 against an expected 52.8);
         *   - leaving a spacer in the card being closed: recovers about a tenth of the
         *     jump, 57% -> 47%;
         *   - deferring the hand-over to the end of the flip with a spacer reserving the
         *     space in the opening card: moves the step to the hand-over rather than
         *     removing it, and buys a timer to get wrong.
         *
         * The cause is structural: there is one Dawn button and it can only be in one
         * card. Removing the step means each card owning its own, which costs Dawn's
         * spinner and sold-out handling on whichever one is not the real submit.
         */
        // Where Dawn rendered it, captured before the first move so it can be put back
        // out of harm's way whenever this element's markup is about to be replaced.
        if (!this.buyButtonsHome && !this.contains(productForm)) {
          this.buyButtonsHome = productForm.parentElement;
        }

        slot.appendChild(productForm);
      }

      /*
       * ⚠️ Get Dawn's form out before replacing this element's markup.
       *
       * placeBuyButtons parks <product-form> inside the selected card, and
       * onVariantChange adopts fresh markup with `innerHTML`, which destroys everything
       * inside — the form, the submit button, and the references product-form.js and
       * product-info.js cached in their constructors. The result is a product page with
       * no add-to-cart at all after a flavour change, and nothing in the console to say
       * so. Putting it back where Dawn rendered it lets the adoption proceed; the
       * render() that follows returns it to whichever card is selected.
       */
      rescueBuyButtons() {
        const productForm = this.querySelector('product-form');
        if (!productForm) return;

        const home = this.buyButtonsHome ?? this.closest('product-info') ?? this.parentElement;
        home?.appendChild(productForm);
      }

      /*
       * aria-pressed is only supported on the button controls. The purchase-option card
       * wrappers are plain divs that share the same data attribute so the whole card is
       * clickable — they mirror the state in data-selected, which is what their CSS reads.
       */
      setPressed(attribute, value) {
        this.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const on = String(element.getAttribute(attribute) === value);
          if (element.tagName === 'BUTTON') element.setAttribute('aria-pressed', on);
          // An <option> has no pressed state — being its select's value IS the state.
          // Assigning it here is also what keeps every per-tier interval select in sync,
          // so a tier change leaves the newly revealed one already showing the current
          // cadence rather than its own first option.
          else if (element.tagName === 'OPTION') element.selected = on === 'true';
          else element.setAttribute('data-selected', on);
        });
      }

      onClick(event) {
        // Mid-swap the markup under the pointer is the *previous* variant's, and
        // render() would write that variant's plan id back over the inputs
        // onVariantChange has already corrected. The cards are about to be
        // replaced anyway, so drop the click rather than act on stale prices.
        if (this.swapPending) return;

        const button = event.target.closest('[data-purchase-option], [data-purchase-switch], [data-tier-option]');
        if (!button || !this.contains(button)) return;

        if (button.dataset.purchaseOption) this.dataset.purchase = button.dataset.purchaseOption;
        else if (button.dataset.purchaseSwitch) this.dataset.purchase = button.dataset.purchaseSwitch;
        else if (button.dataset.tierOption) {
          this.dataset.tier = button.dataset.tierOption;
          this.applyTierDefaultInterval(button);
        }

        this.render();
      }

      /*
       * The interval select is the one control that reports through `change` rather than
       * `click`. Same stale-markup guard as onClick: mid-swap the select under the
       * pointer belongs to the previous variant.
       */
      onChange(event) {
        if (this.swapPending) return;

        const select = event.target.closest('[data-interval-select]');
        if (!select || !this.contains(select)) return;

        this.dataset.interval = select.value;
        this.intervalChosen = true;
        this.render();
      }

      /*
       * A bigger pack is a longer supply, so each tier can open on its own cadence
       * (`zena.bulk_config.defaultInterval`, rendered onto the card as
       * data-tier-default-interval).
       *
       * It applies ONLY until the shopper states a cadence of their own. Once they do,
       * intervalChosen latches and switching packs carries their choice across
       * untouched — which is the behaviour setPressed() was written for and the one to
       * preserve. Snapping unconditionally would silently re-schedule the deliveries of
       * someone who had already said what they wanted, every time they compared packs.
       *
       * The target must be offered by the tier being selected: the per-tier <select>s
       * are rendered from that tier's own allocations, so a cadence with no plan behind
       * it has no <option> and the default is declined rather than assigned. Without
       * that guard a missing allocation would leave dataset.interval naming a cell that
       * render() cannot resolve, and the card would show no price at all.
       */
      applyTierDefaultInterval(button) {
        const target = button.dataset.tierDefaultInterval;

        /*
         * Monthly-supply mode (data-interval-locked, from zena.bulk_config.lockInterval):
         * a pack size has exactly one cadence and there is no control to choose
         * another, so the tier's cadence is applied unconditionally. Liquid has already
         * validated it against the variant's allocations, and there are no <option>
         * elements to probe, so the "offered" check below would wrongly decline it.
         */
        if (this.dataset.intervalLocked === 'true') {
          if (target) this.dataset.interval = target;
          return;
        }

        if (this.intervalChosen) return;
        if (!target || target === this.dataset.interval) return;

        const offered = this.querySelector(
          `[data-tier-key="${CSS.escape(button.dataset.tierOption)}"] [data-interval-option="${CSS.escape(target)}"]`,
        );
        if (offered) this.dataset.interval = target;
      }

      /*
       * Dawn re-renders the whole section on variant change and publishes the parsed
       * document, but only swaps a fixed list of IDs itself. We adopt our own freshly
       * server-rendered markup out of that payload — the same mechanism Dawn uses for
       * `price` — so the new variant's totals arrive already `money`-formatted.
       */
      async onVariantChange({ data }) {
        if (!data || data.sectionId !== this.dataset.sectionId) return;

        // Warming the images below yields, so a second change can land mid-flight.
        // Only the newest one may write to the DOM.
        const swap = (this.pendingSwap = (this.pendingSwap || 0) + 1);

        const source = data.html?.getElementById(this.id);
        if (!source) {
          // The new variant carries no mapped selling plans. Render nothing rather than
          // leaving the previous variant's prices on screen, and give the quantity back
          // to Dawn's selector.
          this.rescueBuyButtons();
          this.hidden = true;
          this.innerHTML = '';
          this.cacheRefs();
          this.claimQuantity(false);
          // Nothing left to swap in, so no later pass will clear this for us.
          this.swapPending = false;
          return;
        }

        /*
         * Correct the form before the paint, not after it.
         *
         * product-info.js rewrites [name=id] to the new variant and re-enables the
         * submit button *before* it publishes variantChange, so by the time we get
         * here the form is already selling the new variant. Waiting for warmImages
         * to sync our two hidden inputs would leave up to IMAGE_WARM_DEADLINE ms in
         * which an add-to-cart posts the new variant id against the previous
         * variant's selling plan and tier. Only the visual swap can afford to lag.
         */
        this.reconcileState(source);
        this.syncFormInputs(source);

        this.swapPending = true;
        await this.warmImages(source);
        if (swap !== this.pendingSwap) return;
        this.swapPending = false;

        this.rescueBuyButtons();
        this.hidden = false;
        this.innerHTML = source.innerHTML;
        this.cacheRefs();
        this.claimQuantity(Boolean(this.qtyInput));
        this.warnOnStayCollision();
        this.reconcileState(source);
        this.render();
        // The adopted markup carries the new variant's prices and labels, so the taller
        // state may be a different height than the one measured at connect.
        this.lockOptionsHeight();
      }

      /*
       * Point the hidden inputs at `source`'s prices without adopting its markup.
       * Used to close the window described in onVariantChange; render() does the
       * same job against the live DOM once the swap has happened.
       *
       * Nothing here has to handle "the incoming variant has no quantity input":
       * `data-bundle-qty` is gated on `bulk_orderable`, which is a PRODUCT
       * metafield (snippets/bundle-save.liquid), and a variant swap never leaves
       * the product — so `this.qtyInput` exists exactly when `source`'s does.
       *
       * ⚠️ If `zena.bulk_orderable` is ever made variant-scoped, disabling our
       * input is NOT the fix. Dawn's selector is still disabled and hidden from
       * the claimQuantity(true) at connect, so disabling ours too would leave
       * neither serializing — an add-to-cart in the window would post no
       * `quantity` at all, Shopify would default it to 1, and the plan input
       * would still carry a tier-N plan. Call `this.claimQuantity(false)` and
       * hand the quantity back properly, or do not touch it.
       */
      syncFormInputs(source) {
        if (this.qtyInput) this.qtyInput.value = this.dataset.tier;

        if (this.planInput) {
          const selected = source.querySelector(
            `[data-bundle-total] [data-price-key="${CSS.escape(this.priceKeyFor(this.dataset.tier))}"]`,
          );
          this.planInput.value = selected?.dataset.planId ?? '';
        }
      }

      /*
       * The tier fan is the only part of the block that costs a round trip to re-create.
       * `innerHTML` throws the live <img> elements away, and a fresh one paints nothing
       * until its bytes arrive — so the strip sat blank for as long as the incoming
       * variant's image took to load, while the box around it kept its height. Fetching
       * and decoding off-DOM first turns the swap into a single frame: the outgoing image
       * stays up until the incoming one is ready to take its place.
       *
       * `loading` is flipped on the way in because that markup was rendered for a *first*
       * paint, where the block can be below the fold. By the time it is being swapped the
       * shopper is looking at it, and a lazy image defers its load past the next layout —
       * which is the delay this method exists to remove.
       *
       * Deduped by src: every copy in a fan is the same URL, and usually every card's is
       * too. srcset and sizes are copied across so the preload picks the same candidate
       * the real element will, rather than warming a width nothing goes on to request.
       */
      warmImages(source) {
        const images = Array.from(source.querySelectorAll('img'));
        images.forEach((img) => img.setAttribute('loading', 'eager'));

        const seen = new Set();
        const decoded = [];
        images.forEach((img) => {
          const src = img.getAttribute('src');
          if (!src || seen.has(src)) return;
          seen.add(src);

          const preload = new Image();
          // sizes before srcset before src — the candidate is chosen as src is assigned.
          if (img.sizes) preload.sizes = img.sizes;
          if (img.srcset) preload.srcset = img.srcset;
          preload.src = src;
          // A broken image must not hold the swap; the <img> will show its own alt.
          decoded.push(preload.decode().catch(() => {}));
        });

        if (!decoded.length) return Promise.resolve();

        /*
         * Deadline as well as images: a slow or dead CDN should cost a blank strip, which
         * is what we had before, and never strand the block showing the previous variant's
         * prices. Whichever finishes first wins.
         */
        return Promise.race([Promise.all(decoded), new Promise((resolve) => setTimeout(resolve, IMAGE_WARM_DEADLINE))]);
      }

      /*
       * Keep the shopper's tier/interval/purchase choice across the swap, but fall back
       * to the freshly rendered defaults for any option the new variant no longer offers.
       */
      reconcileState(source) {
        // Queried against `source`, never `this`: onVariantChange calls this once
        // before the swap, when `this` still holds the previous variant's markup.
        // After the swap the two are equivalent, so the second call is unaffected.
        const offers = (attribute, value) =>
          Boolean(value) && Boolean(source.querySelector(`[${attribute}="${CSS.escape(value)}"]`));

        if (!offers('data-tier-option', this.dataset.tier)) this.dataset.tier = source.dataset.tier;
        if (!offers('data-purchase-option', this.dataset.purchase)) this.dataset.purchase = source.dataset.purchase;

        /*
         * Monthly-supply mode: the cadence is a function of the tier, never a
         * remembered choice, and the fresh markup has no <option>s to probe. Re-derive
         * it from the (possibly reconciled) tier's card. Falling through to the
         * generic branch would reset it to the default tier's cadence while keeping
         * the shopper's tier — and post a 3-pack on the 1-pack's monthly plan.
         */
        if (source.dataset.intervalLocked === 'true') {
          const card = source.querySelector(`[data-tier-option="${CSS.escape(this.dataset.tier)}"]`);
          if (card?.dataset.tierDefaultInterval) this.dataset.interval = card.dataset.tierDefaultInterval;
          return;
        }

        if (!offers('data-interval-option', this.dataset.interval)) this.dataset.interval = source.dataset.interval;
      }
    },
  );
}
